import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Autocomplete,
  FormHelperText,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import {
  collection,
  query,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp
} from 'firebase/firestore';
import { 
  TASK_TYPES, 
  ENTITY_TYPES, 
  PRIORITIES,
  TASK_STATUSES
} from '../../constants/tasksConstants';

const TaskForm = ({ initialData = null, onSubmit, onCancel, preselectedEntity = null }) => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [errors, setErrors] = useState({});
  
  // Stan formularza
  const [formData, setFormData] = useState({
    entityType: preselectedEntity?.type || 'general',
    entityId: preselectedEntity?.id || '',
    assignedTo: currentUser?.uid || '',
    taskType: 'follow_up',
    priority: 'medium',
    description: '',
    suggestedAction: '',
    dueDate: new Date().toISOString().split('T')[0],
    dueTime: new Date().toTimeString().slice(0, 5),
    status: 'pending'
  });

  // Jeśli mamy dane początkowe (edycja), załaduj je
  useEffect(() => {
    if (initialData) {
      const dueDate = initialData.dueDate instanceof Date 
        ? initialData.dueDate 
        : initialData.dueDate.toDate();
      
      setFormData({
        entityType: initialData.entityType || 'general',
        entityId: initialData.entityId || '',
        assignedTo: initialData.assignedTo || currentUser?.uid || '',
        taskType: initialData.taskType || 'follow_up',
        priority: initialData.priority || 'medium',
        description: initialData.description || '',
        suggestedAction: initialData.suggestedAction || '',
        dueDate: dueDate.toISOString().split('T')[0],
        dueTime: dueDate.toTimeString().slice(0, 5),
        status: initialData.status || 'pending'
      });
    }
  }, [initialData]);

  // Pobierz klientów, leady i pracowników przy pierwszym renderowaniu
  useEffect(() => {
    fetchClients();
    fetchLeads();
    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin]);

  const fetchClients = async () => {
    try {
      const clientsQuery = query(collection(db, 'clients'));
      const clientsSnapshot = await getDocs(clientsQuery);
      const clientsList = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClients(clientsList);
    } catch (error) {
      console.error('Błąd podczas pobierania klientów:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      const leadsQuery = query(collection(db, 'leads'));
      const leadsSnapshot = await getDocs(leadsQuery);
      const leadsList = leadsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLeads(leadsList);
    } catch (error) {
      console.error('Błąd podczas pobierania leadów:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      const employeesList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEmployees(employeesList);
    } catch (error) {
      console.error('Błąd podczas pobierania pracowników:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Wyczyść błąd dla tego pola
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleEntitySelect = (event, value) => {
    if (value) {
      setFormData({
        ...formData,
        entityId: value.id
      });
      
      if (errors.entityId) {
        setErrors({
          ...errors,
          entityId: null
        });
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (formData.entityType !== 'general' && !formData.entityId) {
      newErrors.entityId = 'Wybierz klienta lub leada';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Opis zadania jest wymagany';
    }
    
    if (!formData.dueDate) {
      newErrors.dueDate = 'Data wykonania jest wymagana';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Przygotuj dane do zapisania
      const dueDate = new Date(`${formData.dueDate}T${formData.dueTime || '00:00'}`);
      
      const taskData = {
        entityType: formData.entityType,
        entityId: formData.entityType === 'general' ? null : formData.entityId,
        assignedTo: formData.assignedTo,
        createdBy: initialData?.createdBy || currentUser.uid,
        taskType: formData.taskType,
        description: formData.description,
        suggestedAction: formData.suggestedAction,
        priority: formData.priority,
        dueDate: dueDate,
        status: formData.status,
        completedAt: null,
        notesOnCompletion: null,
        createdAt: initialData?.createdAt || new Date(),
        lastUpdate: new Date()
      };
      
      let result;
      
      if (initialData) {
        // Aktualizacja istniejącego zadania
        const taskRef = doc(db, 'tasks', initialData.id);
        await updateDoc(taskRef, taskData);
        result = { id: initialData.id, ...taskData };
      } else {
        // Dodanie nowego zadania
        const tasksRef = collection(db, 'tasks');
        result = await addDoc(tasksRef, taskData);
      }
      
      // Wywołaj callback z danymi
      if (onSubmit) {
        onSubmit({
          id: result.id || result,
          ...taskData
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Błąd podczas zapisywania zadania:', error);
      setLoading(false);
    }
  };

  // Przygotowanie opcji dla autocomplete
  const getEntityOptions = () => {
    if (formData.entityType === 'client') {
      return clients.map(client => ({
        id: client.id,
        label: `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.id
      }));
    } else if (formData.entityType === 'lead') {
      return leads.map(lead => ({
        id: lead.id,
        label: `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.id
      }));
    } else {
      return [];
    }
  };

  // Znajdź wybraną opcję dla autocomplete
  const getSelectedEntity = () => {
    if (!formData.entityId || formData.entityType === 'general') return null;
    
    const options = getEntityOptions();
    return options.find(option => option.id === formData.entityId) || null;
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {/* Typ podmiotu (klient/lead/ogólne) */}
        {!preselectedEntity && (
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <Typography variant="subtitle2" gutterBottom>
                Typ podmiotu
              </Typography>
              <RadioGroup
                row
                name="entityType"
                value={formData.entityType}
                onChange={handleInputChange}
              >
                <FormControlLabel 
                  value="client" 
                  control={<Radio />} 
                  label="Klient" 
                  disabled={!!preselectedEntity}
                />
                <FormControlLabel 
                  value="lead" 
                  control={<Radio />} 
                  label="Lead" 
                  disabled={!!preselectedEntity}
                />
                <FormControlLabel 
                  value="general" 
                  control={<Radio />} 
                  label="Ogólne" 
                  disabled={!!preselectedEntity}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        )}

        {/* Wybór konkretnego klienta/leada */}
        {formData.entityType !== 'general' && (
          <Grid item xs={12}>
            <Autocomplete
              value={getSelectedEntity()}
              options={getEntityOptions()}
              onChange={handleEntitySelect}
              disabled={!!preselectedEntity}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={formData.entityType === 'client' ? 'Wybierz klienta' : 'Wybierz leada'} 
                  placeholder="Wyszukaj..."
                  fullWidth
                  error={!!errors.entityId}
                  helperText={errors.entityId}
                />
              )}
            />
          </Grid>
        )}

        {/* Przypisane do */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Przypisane do</InputLabel>
            <Select
              name="assignedTo"
              value={formData.assignedTo}
              onChange={handleInputChange}
              label="Przypisane do"
              disabled={!isAdmin}
            >
              {isAdmin ? (
                employees.map(employee => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {`${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.id}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value={currentUser.uid}>
                  {currentUser.displayName || currentUser.email || currentUser.uid}
                </MenuItem>
              )}
            </Select>
            {!isAdmin && (
              <FormHelperText>
                Tylko administrator może przypisać zadanie do innego pracownika
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Typ zadania */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Typ zadania</InputLabel>
            <Select
              name="taskType"
              value={formData.taskType}
              onChange={handleInputChange}
              label="Typ zadania"
            >
              {Object.entries(TASK_TYPES).map(([key, value]) => (
                <MenuItem key={key} value={key}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Priorytet */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Priorytet</InputLabel>
            <Select
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              label="Priorytet"
            >
              {Object.entries(PRIORITIES).map(([key, value]) => (
                <MenuItem key={key} value={key}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Status */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              label="Status"
            >
              {Object.entries(TASK_STATUSES).map(([key, value]) => (
                <MenuItem key={key} value={key}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Data i czas wykonania */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Data wykonania"
            name="dueDate"
            type="date"
            value={formData.dueDate}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
            error={!!errors.dueDate}
            helperText={errors.dueDate}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Czas wykonania"
            name="dueTime"
            type="time"
            value={formData.dueTime}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Opis */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Opis zadania"
            name="description"
            multiline
            rows={4}
            value={formData.description}
            onChange={handleInputChange}
            error={!!errors.description}
            helperText={errors.description}
          />
        </Grid>

        {/* Sugerowana akcja */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Sugerowana akcja (opcjonalnie)"
            name="suggestedAction"
            multiline
            rows={2}
            value={formData.suggestedAction}
            onChange={handleInputChange}
            placeholder="Np. 'Zadzwoń do klienta i zapytaj o...' lub 'Przygotuj ofertę zawierającą...'"
          />
        </Grid>

        {/* Przyciski */}
        <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={onCancel}
            disabled={loading}
          >
            Anuluj
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : initialData ? 'Zapisz zmiany' : 'Dodaj zadanie'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TaskForm;
